import { appConfig } from '../../config/env'
import { getAccessToken } from '../auth/token'
import { ApiError } from './error'
import type { ErpResponse, QueryParams, QueryValue, RequestOptions } from './types'

const DEFAULT_TIMEOUT = 15000
const SUCCESS_CODES = new Set<number | string>([0, '0', 'SUCCESS'])

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

function joinUrl(baseUrl: string, path: string): string {
  if (isAbsoluteUrl(path)) {
    return path
  }

  const normalizedBase = baseUrl.replace(/\/$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  return `${normalizedBase}${normalizedPath}`
}

function appendQuery(url: string, params?: QueryParams): string {
  if (!params) {
    return url
  }

  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    appendQueryValue(searchParams, key, value)
  })

  const query = searchParams.toString()

  if (!query) {
    return url
  }

  return `${url}${url.includes('?') ? '&' : '?'}${query}`
}

function appendQueryValue(
  searchParams: URLSearchParams,
  key: string,
  value: QueryValue,
): void {
  if (value === null || value === undefined || value === '') {
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      searchParams.append(key, String(item))
    })
    return
  }

  searchParams.append(key, String(value))
}

function isBodyInit(data: unknown): data is BodyInit {
  return (
    data instanceof FormData ||
    data instanceof Blob ||
    data instanceof ArrayBuffer ||
    data instanceof URLSearchParams ||
    data instanceof ReadableStream
  )
}

function createBody(data: unknown): BodyInit | undefined {
  if (data === undefined || data === null) {
    return undefined
  }

  if (isBodyInit(data)) {
    return data
  }

  return JSON.stringify(data)
}

function createHeaders(options: RequestOptions): Headers {
  const headers = new Headers(options.headers)
  const token = getAccessToken()

  if (!options.skipAuth && token && !headers.has('Authorization')) {
    headers.set('Authorization', token)
    headers.set('type', 'b')
  }

  if (
    options.data !== undefined &&
    options.data !== null &&
    !isBodyInit(options.data) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json')
  }

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json')
  }

  return headers
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isErpResponse<T>(payload: unknown): payload is ErpResponse<T> {
  return (
    isObject(payload) &&
    ('code' in payload || 'success' in payload || 'data' in payload)
  )
}

function getResponseMessage<T>(payload: ErpResponse<T>): string {
  return payload.message || payload.msg || '接口请求失败'
}

function isSuccessResponse<T>(payload: ErpResponse<T>): boolean {
  if (payload.success === false) {
    return false
  }

  if (payload.code === undefined || payload.code === null || payload.code === '') {
    return true
  }

  if (typeof payload.code === 'number') {
    return (payload.code >= 200 && payload.code <= 299) || SUCCESS_CODES.has(payload.code)
  }

  return SUCCESS_CODES.has(payload.code)
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined
  }

  const contentType = response.headers.get('Content-Type') || ''

  if (contentType.includes('application/json')) {
    return response.json()
  }

  return response.text()
}

async function request<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController()
  const timeout = options.timeout ?? DEFAULT_TIMEOUT
  const timer = window.setTimeout(() => controller.abort(), timeout)
  const {
    data,
    params,
    timeout: _timeout,
    skipAuth: _skipAuth,
    unwrap,
    ...fetchOptions
  } = options
  const requestUrl = appendQuery(joinUrl(appConfig.apiBaseUrl, url), params)

  try {
    const response = await fetch(requestUrl, {
      ...fetchOptions,
      method: options.method || 'GET',
      headers: createHeaders(options),
      body: createBody(data),
      credentials: 'include',
      signal: controller.signal,
    })

    const payload = await parseResponse(response)

    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = '/sign-in'
      }

      throw new ApiError({
        status: response.status,
        message: response.statusText || '网络请求失败',
        data: payload,
      })
    }

    if (!isErpResponse<T>(payload)) {
      return payload as T
    }

    if (unwrap === false) {
      return payload as T
    }

    if (!isSuccessResponse(payload)) {
      if (Number(payload.code) === 401) {
        window.location.href = '/sign-in'
      }

      throw new ApiError({
        code: payload.code,
        status: response.status,
        message: getResponseMessage(payload),
        data: payload,
      })
    }

    return payload.data as T
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError({ message: '接口请求超时' })
    }

    throw new ApiError({
      message: error instanceof Error ? error.message : '接口请求异常',
    })
  } finally {
    window.clearTimeout(timer)
  }
}

export const http = {
  get<T>(url: string, options?: Omit<RequestOptions, 'method' | 'data'>) {
    return request<T>(url, { ...options, method: 'GET' })
  },
  post<T>(url: string, data?: unknown, options?: Omit<RequestOptions, 'method' | 'data'>) {
    return request<T>(url, { ...options, method: 'POST', data })
  },
  put<T>(url: string, data?: unknown, options?: Omit<RequestOptions, 'method' | 'data'>) {
    return request<T>(url, { ...options, method: 'PUT', data })
  },
  patch<T>(url: string, data?: unknown, options?: Omit<RequestOptions, 'method' | 'data'>) {
    return request<T>(url, { ...options, method: 'PATCH', data })
  },
  delete<T>(url: string, options?: Omit<RequestOptions, 'method' | 'data'>) {
    return request<T>(url, { ...options, method: 'DELETE' })
  },
  request,
}
